import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ScrollReveal from '@/components/base/ScrollReveal';

const FEATURES = [
  { key: 'heroFeature1', icon: 'ri-check-fill' },
  { key: 'heroFeature2', icon: 'ri-check-fill' },
  { key: 'heroFeature3', icon: 'ri-check-fill' },
  { key: 'heroFeature4', icon: 'ri-check-fill' },
];

const TRUST_BADGES = [
  { key: 'trust1', icon: 'ri-shield-check-line' },
  { key: 'trust2', icon: 'ri-star-line' },
  { key: 'trust3', icon: 'ri-time-line' },
];

const KPI_CARDS = [
  { labelKey: 'kpiRoas', value: '4.8x', color: 'from-primary-500/10 to-primary-500/5 border-primary-200/40', glowColor: 'shadow-[0_0_20px_rgba(229,57,53,0.15)]', icon: 'ri-funds-line', iconColor: 'text-primary-500', delay: '0s' },
  { labelKey: 'kpiLeads', value: '247', color: 'from-accent-500/10 to-accent-500/5 border-accent-300/40', glowColor: 'shadow-[0_0_20px_rgba(200,162,74,0.15)]', icon: 'ri-user-add-line', iconColor: 'text-accent-500', delay: '0.8s' },
  { labelKey: 'kpiCtr', value: '3.2%', color: 'from-foreground-100/80 to-foreground-50/40 border-foreground-200/40', glowColor: '', icon: 'ri-mouse-line', iconColor: 'text-foreground-600', delay: '1.6s' },
  { labelKey: 'kpiConversions', value: '89', color: 'from-primary-500/10 to-primary-500/5 border-primary-200/40', glowColor: 'shadow-[0_0_20px_rgba(229,57,53,0.12)]', icon: 'ri-bar-chart-2-line', iconColor: 'text-primary-500', delay: '2.4s' },
];

export default function Hero() {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen flex items-center bg-white overflow-hidden pt-24 md:pt-28 pb-12 md:pb-16">
      <div className="absolute top-0 right-0 w-[600px] md:w-[900px] h-[600px] md:h-[900px] bg-gradient-to-bl from-primary-50/60 via-accent-50/30 to-transparent rounded-full blur-3xl -translate-y-1/4 translate-x-1/4 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-gradient-to-tr from-accent-50/40 via-transparent to-transparent rounded-full blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none"></div>

      <div className="relative w-full max-w-7xl mx-auto px-4 md:px-10">
        {/* Video primero, grande y centrado */}
        <ScrollReveal>
          <div className="relative max-w-4xl mx-auto mb-10 md:mb-14">
            <div className="relative">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-gray-100 bg-white flex items-center justify-center">
                <video
                  src="https://storage.helloreaddy.io/project_files/3dceec99-0a90-4550-9724-ce762c1f57f1/ed3d093c-cc43-4d28-b660-df1205b20460_VSL-Plan-Traffic-mp4.mp4"
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="w-full h-auto object-contain"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/5 via-transparent to-transparent pointer-events-none"></div>
              </div>

              {KPI_CARDS.map((kpi, i) => (
                <div
                  key={kpi.labelKey}
                  className={`absolute z-20 hidden md:block bg-white/80 backdrop-blur-xl border ${kpi.color} rounded-xl px-4 py-3 ${kpi.glowColor} animate-float`}
                  style={{
                    animationDelay: kpi.delay,
                    animationDuration: `${5 + i * 0.5}s`,
                    ...(i === 0 ? { top: '-5%', right: '-8%' } : {}),
                    ...(i === 1 ? { bottom: '15%', left: '-10%' } : {}),
                    ...(i === 2 ? { top: '30%', right: '-12%' } : {}),
                    ...(i === 3 ? { bottom: '40%', left: '-8%' } : {}),
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <i className={`${kpi.icon} ${kpi.iconColor} text-sm`}></i>
                    <span className="text-foreground-500 text-[10px] uppercase tracking-wider font-semibold">
                      {t(`trafficPro.${kpi.labelKey}`)}
                    </span>
                  </div>
                  <span className="font-heading text-xl font-bold text-foreground-950 block">
                    {kpi.value}
                  </span>
                </div>
              ))}
            </div>

            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-accent-500/10 rounded-full blur-2xl pointer-events-none animate-pulse-glow"></div>
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-primary-500/10 rounded-full blur-2xl pointer-events-none animate-pulse-glow" style={{ animationDelay: '1.5s' }}></div>

            <div className="mt-6 text-center">
              <a
                href="https://wa.link/em25v1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-red-600 text-white text-base font-bold hover:bg-red-700 transition-all duration-300 whitespace-nowrap shadow-[0_4px_20px_rgba(220,38,38,0.45)] hover:shadow-[0_8px_30px_rgba(220,38,38,0.55)] hover:-translate-y-0.5 animate-pulse-subtle"
              >
                <i className="ri-rocket-line text-base"></i>
                {t('trafficPro.heroSpeedCTA')}
              </a>
            </div>
          </div>
        </ScrollReveal>

        {/* Texto debajo del video */}
        <div className="max-w-3xl mx-auto text-center">
          <ScrollReveal>
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold tracking-wider uppercase mb-5 border border-primary-100">
              {t('trafficPro.badge')}
            </span>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-[1.08] mb-5">
              {t('trafficPro.heroTitle')}
              <br />
              <span className="text-primary-500">{t('trafficPro.heroHeadline')}</span>
            </h1>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-7 max-w-xl mx-auto">
              {t('trafficPro.heroSubtitle')}
            </p>
          </ScrollReveal>

          <ScrollReveal delay={0.25}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5 mb-8 max-w-xl mx-auto">
              {FEATURES.map((f) => (
                <div key={f.key} className="flex items-center justify-center sm:justify-start gap-2">
                  <i className={`${f.icon} text-primary-500 text-sm`}></i>
                  <span className="text-foreground-700 text-sm font-medium">{t(`trafficPro.${f.key}`)}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.3}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
              <Link
                to="https://wa.link/em25v1"
                className="px-7 py-3.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap shadow-[0_4px_16px_rgba(229,57,53,0.3)] hover:shadow-[0_6px_24px_rgba(229,57,53,0.4)] hover:-translate-y-0.5 inline-flex items-center gap-2"
              >
                {t('trafficPro.ctaPrimary')}
                <i className="ri-arrow-right-line"></i>
              </Link>
              <a
                href="#pricing"
                className="px-7 py-3.5 rounded-full border-2 border-foreground-200 text-foreground-800 text-sm font-semibold hover:border-primary-300 hover:text-primary-500 transition-all duration-300 whitespace-nowrap inline-flex items-center gap-2"
              >
                {t('trafficPro.ctaSecondary')}
                <i className="ri-arrow-down-line"></i>
              </a>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.35}>
            <div className="flex flex-wrap items-center justify-center gap-5">
              {TRUST_BADGES.map((tb) => (
                <div key={tb.key} className="flex items-center gap-2">
                  <i className={`${tb.icon} text-foreground-400 text-sm`}></i>
                  <span className="text-foreground-500 text-xs">{t(`trafficPro.${tb.key}`)}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}